import { supabase } from './supabase';

export interface SuspiciousDevice {
  device_fingerprint: string;
  user_count: number;
  users: {
    user_id: string;
    display_name: string;
    username: string;
    vote_count: number;
    first_vote: string;
    last_vote: string;
  }[];
  total_votes: number;
  polls_voted: string[];
}

export interface SuspiciousUser {
  user_id: string;
  display_name: string;
  username: string;
  device_count: number;
  devices: string[];
  total_votes: number;
  account_age_hours: number;
  votes_per_hour: number;
}

export interface AbuseReport {
  suspicious_devices: SuspiciousDevice[];
  suspicious_users: SuspiciousUser[];
  summary: {
    total_users_flagged: number;
    total_devices_flagged: number;
    total_suspicious_votes: number;
  };
}

class AbuseDetectionAdminService {
  /**
   * Get full abuse detection report
   */
  async getAbuseReport(): Promise<AbuseReport> {
    const [suspiciousDevices, suspiciousUsers] = await Promise.all([
      this.getSuspiciousDevices(),
      this.getSuspiciousUsers(),
    ]);

    const totalSuspiciousVotes = suspiciousDevices.reduce(
      (sum, device) => sum + device.total_votes,
      0
    );

    return {
      suspicious_devices: suspiciousDevices,
      suspicious_users: suspiciousUsers,
      summary: {
        total_users_flagged: suspiciousUsers.length,
        total_devices_flagged: suspiciousDevices.length,
        total_suspicious_votes: totalSuspiciousVotes,
      },
    };
  }

  /**
   * Detect multiple accounts from same device
   * Flags devices with 2+ different users
   */
  private async getSuspiciousDevices(): Promise<SuspiciousDevice[]> {
    // Get all votes grouped by device_fingerprint
    const { data: deviceVotes, error } = await supabase
      .from('votes')
      .select(`
        device_fingerprint,
        user_id,
        poll_id,
        created_at
      `)
      .not('device_fingerprint', 'is', null)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get unique user IDs
    const userIds = Array.from(new Set((deviceVotes || []).map((v: any) => v.user_id)));

    // Fetch profiles for all users
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, username')
      .in('id', userIds);

    // Create a map of user profiles
    const profilesMap = new Map(
      (profiles || []).map((p: any) => [p.id, p])
    );

    // Group by device fingerprint
    const devicesMap = new Map<string, any>();

    (deviceVotes || []).forEach((vote: any) => {
      const deviceId = vote.device_fingerprint;
      if (!devicesMap.has(deviceId)) {
        devicesMap.set(deviceId, {
          device_fingerprint: deviceId,
          users: new Map(),
          total_votes: 0,
          polls: new Set(),
        });
      }

      const device = devicesMap.get(deviceId);
      device.total_votes++;
      device.polls.add(vote.poll_id);

      const profile = profilesMap.get(vote.user_id);
      if (!device.users.has(vote.user_id)) {
        device.users.set(vote.user_id, {
          user_id: vote.user_id,
          display_name: profile?.display_name || 'Unknown',
          username: profile?.username || 'unknown',
          vote_count: 0,
          first_vote: vote.created_at,
          last_vote: vote.created_at,
        });
      }

      const user = device.users.get(vote.user_id);
      user.vote_count++;
      user.last_vote = vote.created_at;
    });

    // Filter devices with 2+ users (suspicious)
    const suspiciousDevices: SuspiciousDevice[] = [];

    devicesMap.forEach((device) => {
      if (device.users.size >= 2) {
        suspiciousDevices.push({
          device_fingerprint: device.device_fingerprint,
          user_count: device.users.size,
          users: Array.from(device.users.values()),
          total_votes: device.total_votes,
          polls_voted: Array.from(device.polls),
        });
      }
    });

    // Sort by user count (most suspicious first)
    return suspiciousDevices.sort((a, b) => b.user_count - a.user_count);
  }

  /**
   * Detect users voting from multiple devices
   * Flags users with 3+ devices (possible bot/farmer)
   */
  private async getSuspiciousUsers(): Promise<SuspiciousUser[]> {
    // Get all votes grouped by user
    const { data: userVotes, error } = await supabase
      .from('votes')
      .select(`
        user_id,
        device_fingerprint,
        created_at
      `)
      .not('device_fingerprint', 'is', null);

    if (error) throw error;

    // Get unique user IDs
    const userIds = Array.from(new Set((userVotes || []).map((v: any) => v.user_id)));

    // Fetch profiles for all users
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, username, created_at')
      .in('id', userIds);

    // Create a map of user profiles
    const profilesMap = new Map(
      (profiles || []).map((p: any) => [p.id, p])
    );

    // Group by user
    const usersMap = new Map<string, any>();

    (userVotes || []).forEach((vote: any) => {
      const profile = profilesMap.get(vote.user_id);
      if (!usersMap.has(vote.user_id)) {
        usersMap.set(vote.user_id, {
          user_id: vote.user_id,
          display_name: profile?.display_name || 'Unknown',
          username: profile?.username || 'unknown',
          devices: new Set(),
          total_votes: 0,
          created_at: profile?.created_at,
        });
      }

      const user = usersMap.get(vote.user_id);
      user.devices.add(vote.device_fingerprint);
      user.total_votes++;
    });

    // Filter users with 3+ devices (suspicious)
    const suspiciousUsers: SuspiciousUser[] = [];

    usersMap.forEach((user) => {
      if (user.devices.size >= 3) {
        const accountAge = user.created_at
          ? (Date.now() - new Date(user.created_at).getTime()) / (1000 * 60 * 60)
          : 0;

        suspiciousUsers.push({
          user_id: user.user_id,
          display_name: user.display_name,
          username: user.username,
          device_count: user.devices.size,
          devices: Array.from(user.devices),
          total_votes: user.total_votes,
          account_age_hours: Math.round(accountAge),
          votes_per_hour: accountAge > 0 ? user.total_votes / accountAge : 0,
        });
      }
    });

    // Sort by device count (most suspicious first)
    return suspiciousUsers.sort((a, b) => b.device_count - a.device_count);
  }

  /**
   * Get detailed device report
   */
  async getDeviceReport(deviceFingerprint: string) {
    const { data: votes, error } = await supabase
      .from('votes')
      .select(`
        *,
        polls(id, title),
        poll_options(text, contestants(full_name))
      `)
      .eq('device_fingerprint', deviceFingerprint)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get unique user IDs from votes
    const userIds = Array.from(new Set((votes || []).map((v: any) => v.user_id)));

    // Fetch profiles for all users
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, username, avatar_url, created_at')
      .in('id', userIds);

    // Create a map of user profiles
    const profilesMap = new Map(
      (profiles || []).map((p: any) => [p.id, p])
    );

    // Attach profiles to votes
    const votesWithProfiles = (votes || []).map((vote: any) => ({
      ...vote,
      profiles: profilesMap.get(vote.user_id),
    }));

    return votesWithProfiles;
  }

  /**
   * Get detailed user voting pattern
   */
  async getUserVotingPattern(userId: string) {
    const { data, error } = await supabase
      .from('votes')
      .select(`
        *,
        polls(id, title),
        poll_options(text, contestants(full_name))
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Get unique devices
    const devices = new Set((data || []).map((v: any) => v.device_fingerprint));

    return {
      votes: data,
      device_count: devices.size,
      devices: Array.from(devices),
    };
  }
}

export const abuseDetectionAdminService = new AbuseDetectionAdminService();
